import {
  roundDecimal,
  roundDynamic,
  roundMultiple,
} from "../utilityFunctions/utilityFunctions";

export function popDec(
  wpershw: number,
  paramYearlyDecrease: number,
  paramObservPeriod: number
): number {
  return wpershw * (1 - paramYearlyDecrease) ** paramObservPeriod;
}

export function popInc(
  wpershw: number,
  paramYearlyDecrease: number,
  paramObservPeriod: number
): number {
  return wpershw * (1 + paramYearlyDecrease) ** paramObservPeriod;
}

export function popTh(
  wpershw: number,
  wohnflEinw: number,
  paramAreaPerResident: number
): number {
  return (wpershw * wohnflEinw) / paramAreaPerResident;
}

export function wohnflEinw(wohnflBest: number, wPersHw: number) {
  if (wPersHw === 0) {
    return 0;
  }

  return wohnflBest / wPersHw;
}

export function populationVariation(
  wpershw: number,
  popDec: number,
  popInc: number,
  popTh: number
): number {
  if (popTh > popInc) {
    return popInc - wpershw;
  }

  if (popTh < popDec) {
    return popDec - wpershw;
  }

  return popTh - wpershw;
}

export function avgBuildingHeight(fahoeMax: number, geshoe: number): number {
  if (fahoeMax === 0 && geshoe !== 0) {
    return geshoe;
  } else if (fahoeMax !== 0 && geshoe === 0) {
    return fahoeMax;
  } else {
    return (fahoeMax + geshoe) / 2;
  }
}

export function populationTemporary(
  apartmentInfo: {
    wohnWnart: number;
    paramPopulationPerApartment: number;
  }[]
): number {
  return apartmentInfo.reduce(
    (value, item) => value + item.wohnWnart * item.paramPopulationPerApartment,
    0
  );
}

export function populationPending(
  apartmentInfo: {
    wohnWnart: number;
    paramPopulationPerApartment: number;
  }[]
): number {
  return apartmentInfo.reduce(
    (value, item) => value + item.wohnWnart * item.paramPopulationPerApartment,
    0
  );
}

export function populationRelevant(
  wpershw: number,
  populationTemporary: number,
  populationPending: number
): number {
  return wpershw + populationTemporary + populationPending;
}

export function areaRealizedCovered(
  wohnflBest: number,
  wohnflImbBew: number
): number {
  return wohnflBest + wohnflImbBew;
}

export function areaPotCoveredAz(
  fBebaut: number,
  az: number,
  varHabitationRatioFinal: number,
  paramDevelopRatioCovered: number
): number {
  return (
    fBebaut * 10000 * az * varHabitationRatioFinal * paramDevelopRatioCovered
  );
}

export function areaPotCoveredUez(
  fBebaut: number,
  avgBuildingHeightFinal: number,
  varUezFinal: number,
  paramUezRatio: number,
  varHabitationRatioFinal: number,
  paramDevelopRatioCovered: number
): number {
  return (
    ((fBebaut * 10000 * avgBuildingHeightFinal * varUezFinal) / paramUezRatio) *
    varHabitationRatioFinal *
    paramDevelopRatioCovered
  );
}

export function areaPotUncoveredUez(
  fUnbebaut: number,
  avgBuildingHeightFinal: number,
  varUezFinal: number,
  paramUezRatio: number,
  varHabitationRatioFinal: number,
  paramDevelopRatioUncovered: number
): number {
  return (
    ((fUnbebaut * 10000 * avgBuildingHeightFinal * varUezFinal) /
      paramUezRatio) *
    varHabitationRatioFinal *
    paramDevelopRatioUncovered
  );
}

export function areaPotUncoveredAz(
  fUnbebaut: number,
  az: number,
  varHabitationRatioFinal: number,
  paramDevelopRatioUncovered: number
): number {
  return (
    fUnbebaut *
    10000 *
    az *
    varHabitationRatioFinal *
    paramDevelopRatioUncovered
  );
}

export function populationPotCovered(
  areaPotCovered: number,
  areaRealizedCovered: number,
  paramAreaPerResident: number,
  paramDensificationRatioCovered: number
): number {
  return Math.max(
    0,
    ((areaPotCovered - areaRealizedCovered) / paramAreaPerResident) *
      paramDensificationRatioCovered
  );
}

export function populationPotTotalCovered(
  populationPotCovered: number,
  populationRelevant: number
): number {
  return populationPotCovered + populationRelevant;
}

export function populationCapacityCovered(
  populationPotTotalCovered: number,
  populationVariation: number
): number {
  return populationPotTotalCovered + populationVariation;
}

export function populationCapacity(
  populationCapacityCovered: number,
  populationCapacityUncovered: number
): number {
  return populationCapacityCovered + populationCapacityUncovered;
}

export function populationPotUncovered(
  areaPotUncovered: number,
  paramAreaPerResident: number
): number {
  return Math.max(0, areaPotUncovered / paramAreaPerResident);
}

export function populationCapacityUncovered(
  populationPotUncovered: number,
  paramDensificationRatioUncovered: number
): number {
  return populationPotUncovered * paramDensificationRatioUncovered;
}

export function lBuildingAreaDemandActual(
  fBebaut: number,
  populationRelevant: number
): number {
  return roundMultiple((fBebaut * 10000) / populationRelevant, 5);
}

export function mBuildingAreaDemandExpected(
  fTotal: number,
  populationCapacity: number
): number {
  return roundMultiple((fTotal * 10000) / populationCapacity, 5);
}

export function bPopulationCapacityTotal(
  populationCapacity: number,
  populationCorrection: number,
  populationTemporary: number
): number {
  return roundDynamic(
    populationCapacity + populationCorrection - populationTemporary
  );
}

export function cPopulationGrowth(
  bPopulationCapacityTotal: number,
  populationKgwrFinal: number
): number {
  return roundDynamic(bPopulationCapacityTotal - populationKgwrFinal);
}

export function ePopulationGrowthAnnual(
  cPopulationGrowth: number,
  paramObservPeriod: number
): number {
  return roundDecimal(cPopulationGrowth / paramObservPeriod, 0);
}

export function rPopulationNewZonesDemand(
  bPopulationCapacityTotal: number,
  qPopulationFuture: number
): number {
  return bPopulationCapacityTotal - qPopulationFuture;
}

export function dPopulationGrowthPercent(
  cPopulationGrowth: number,
  populationKgwrFinal: number
): number {
  return roundDecimal((cPopulationGrowth / populationKgwrFinal) * 100, 1);
}

export function fPopulationGrowthAnnualPercent(
  bPopulationCapacityTotal: number,
  populationKgwrFinal: number,
  paramObservPeriod: number
): number {
  return roundDecimal(
    ((bPopulationCapacityTotal / populationKgwrFinal) **
      (1 / paramObservPeriod) -
      1) *
      100,
    1
  );
}

export function qPopulationFuture(
  populationHist: number,
  pVarFactorRp: number,
  paramObservPeriod: number,
  paramRelevantYearPopulation: number
): number {
  return roundDynamic(
    populationHist *
      (1 + pVarFactorRp / 100) **
        (new Date().getFullYear() +
          paramObservPeriod -
          paramRelevantYearPopulation)
  );
}

export function populationKgwrFinal(
  aPopulationActual: number,
  populationHist: number
): number {
  return Math.max(aPopulationActual, populationHist);
}

export function populationCorrection(
  populationKgwrFinal: number,
  aPopulationActual: number
): number {
  return populationKgwrFinal - aPopulationActual;
}

export function compensation(
  mBuildingAreaDemandExpected: number,
  oBuildingAreaDemandComparison: number,
  rPopulationNewZonesDemand: number,
  bPopulationCapacityTotal: number
): number {
  return mBuildingAreaDemandExpected > oBuildingAreaDemandComparison ||
    rPopulationNewZonesDemand / bPopulationCapacityTotal > 0.03
    ? 1
    : 0;
}
